import cuid from "cuid";
import { FieldArray, Formik, useFormikContext } from "formik";
import { get } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { PROCESS_TAGS, PROCESS_TYPES, TEMPLATES } from "utils/constants";
import { bindArrayInputProps } from "utils/helpers/input";
import { sortProcessStatuses } from "../../sortProcessStatuses";

// Helper component to sync form values to parent
const FormSyncEffect = ({ onProcessStatusesChange }) => {
  const { values } = useFormikContext();
  const processStatusesJson = JSON.stringify(values.processStatuses);

  useEffect(() => {
    if (onProcessStatusesChange) {
      onProcessStatusesChange(sortProcessStatuses(values.processStatuses));
    }
  }, [processStatusesJson, onProcessStatusesChange]);

  return null;
};

const Statuses = ({
  formRef,
  dirtyRef,
  setIsDirty,
  TABS,
  onSubmit,
  type,
  theme,
  onProcessStatusesChange,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const TEMPLATES_LABELS = t("TEMPLATES", { returnObjects: true });
  const TAGS = t("PROCESS_TAGS", { returnObjects: true });

  return (
    <Formik
      innerRef={(formik) => {
        formRef.current = formik;
      }}
      initialValues={{
        processStatuses: [],
      }}
      onSubmit={async (values) => {
        await onSubmit({
          type: type || PROCESS_TYPES.B2B,
          name: "",
          theme: theme || null,
          description: "",
          skills: [],
          config: [],
          attributionType: null,
          processStatuses: values.processStatuses,
          insurancePlans: [],
        });
        setIsDirty((prev) => ({ ...prev, [TABS.STATUSES]: false }));
      }}
    >
      {({
        values: statusesValues,
        setFieldValue: setStatusesFieldValue,
        dirty,
        ...statusesFormProps
      }) => {
        if (dirtyRef.current !== dirty) {
          dirtyRef.current = dirty;
          setIsDirty((prev) => ({ ...prev, [TABS.STATUSES]: dirty }));
        }

        return (
          <Card type="simple" head={false} title={FORM.STATUSES_INFO}>
            <FormSyncEffect onProcessStatusesChange={onProcessStatusesChange} />
            <div className="process-statuses d-flex flex--column gap-16 w-100">
              <FieldArray
                name={"processStatuses"}
                render={(arrayHelpers) => [
                  statusesValues.processStatuses.map((status, i) => (
                    <div
                      key={status.id}
                      className="process-statuses__row d-flex align--center gap-16 w-100"
                    >
                      <div className="process-statuses__field process-statuses__field--name">
                        <Input
                          placeholder={FORM.STATUSES.NAME}
                          label={FORM.STATUSES.NAME}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "name",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--tag">
                        <Select
                          showSearch
                          type="simple"
                          label={FORM.STATUSES.TAG}
                          options={Object.entries(PROCESS_TAGS).map(
                            ([key, value]) => ({
                              value,
                              label: TAGS[key],
                            }),
                          )}
                          placeholder={FORM.STATUSES.TAG}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "tag",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--progress">
                        <Input
                          placeholder={FORM.STATUSES.PROGRESS}
                          label={FORM.STATUSES.PROGRESS}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "progress",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--order">
                        <Input
                          placeholder={FORM.STATUSES.ORDER}
                          label={FORM.STATUSES.ORDER}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "order",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--template">
                        <Select
                          showSearch
                          options={Object.entries(
                            get(
                              TEMPLATES,
                              `${type || PROCESS_TYPES.B2B}.THEMES.${theme || ""}`,
                              {},
                            ),
                          ).map(([key, value]) => ({
                            label: TEMPLATES_LABELS[key],
                            value,
                          }))}
                          placeholder={FORM.STATUSES.TEMPLATE}
                          label={FORM.STATUSES.TEMPLATE}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "template",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--help-delay">
                        <Input
                          type="number"
                          min={1}
                          placeholder={FORM.STATUSES.HELP_PROMPT_DELAY}
                          label={FORM.STATUSES.HELP_PROMPT_DELAY}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "config.helpPromptDelay",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                          value={
                            statusesValues.processStatuses[i]?.config
                              ?.helpPromptDelay ?? ""
                          }
                          onChange={({ target: { value } }) =>
                            setStatusesFieldValue(
                              `processStatuses.${i}.config.helpPromptDelay`,
                              value === "" ? null : Number(value),
                            )
                          }
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--move">
                        <Input
                          type="checkbox"
                          placeholder={FORM.STATUSES.MOVE_TO_NEXT}
                          label={FORM.STATUSES.MOVE_TO_NEXT}
                          {...bindArrayInputProps({
                            parent: "processStatuses",
                            index: i,
                            name: "moveToNext",
                            values: statusesValues,
                            ...statusesFormProps,
                          })}
                          checked={
                            statusesValues.processStatuses[i]?.moveToNext ||
                            false
                          }
                        />
                      </div>
                      <div className="process-statuses__field process-statuses__field--delete delete-icon">
                        <div
                          onClick={() => {
                            arrayHelpers.remove(i);
                          }}
                        >
                          <BiTrash color={COLORS.C_DANGER} size={24} />
                        </div>
                      </div>
                    </div>
                  )),
                  <Button
                    key="add-row"
                    onClick={() => {
                      arrayHelpers.push({
                        id: cuid(),
                        possibleStatuses: [],
                        actions: [],
                      });
                    }}
                    type="primary"
                  >
                    <BiPlus size={24} />
                  </Button>,
                ]}
              />
            </div>
          </Card>
        );
      }}
    </Formik>
  );
};

export default Statuses;
